import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import dayjs from "dayjs";
import axiosInstance from "../../../services/axiosInstance";
import { SUPPORTED_CALL_LANGS, MAX_FLOW_LANGS } from "../../../i18n/callFlowLangs.js";
// Every screen shows times in the business's own zone (4.3d.4) — set when the profile arrives and when it is saved.
import { setBusinessTimeZone } from "../../../utils/times";

const POSTS_URL = "/heycallcenter/";
const initialState = {
  profile: {
    // locales: keyed by lang code — { en: { name, welcomeMessage }, ar: {...}, fr: {...} }
    locales: {},
    languages: ["en"],
    dLanguage: "en",
    workingDays: {},
    workingHours: {
      from: dayjs().hour(9).minute(0).format("HH:mm"),
      to:   dayjs().hour(17).minute(0).format("HH:mm"),
    },
    isCustomWorkingDays: false,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  },
  status: "idle",
  error: null,
};

export const getProfile = createAsyncThunk(
  POSTS_URL + "api/profile",
  async (_, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(POSTS_URL + "api/profile", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        const d = response.data;
        d.workingDays         = JSON.parse(d.workingDays);
        d.workingHours        = JSON.parse(d.workingHours);
        d.languages           = d.languages.split(",");
        d.isCustomWorkingDays = JSON.parse(d.isCustomWorkingDays);
        d.timezone            = d.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
        setBusinessTimeZone(d.timezone);
        // locales comes pre-parsed from the server; guard against string or missing
        if (typeof d.locales === "string") { try { d.locales = JSON.parse(d.locales); } catch { d.locales = {}; } }
        if (!d.locales || typeof d.locales !== "object") d.locales = {};
        return d;
      } else return null;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

// Saves profile metadata (languages, welcome messages, working hours) to the
// backend without generating TTS audio. Called on every language/welcome change
// so settings persist without requiring a full Deploy.
export const saveProfileMeta = createAsyncThunk(
  "profile/saveMeta",
  async (_, thunkAPI) => {
    const state   = thunkAPI.getState();
    const profile = state.profile.profile;
    const userId  = state.auth?.user?.id;
    if (!userId) return;

    const localesObj = {};
    for (const { code } of SUPPORTED_CALL_LANGS) {
      if (profile.languages.includes(code)) {
        const wp = profile.locales?.[code]?.welcomeParts;
        localesObj[code] = {
          name:               (profile.locales?.[code]?.name           || "").trim(),
          welcomeMessage:     (profile.locales?.[code]?.welcomeMessage || "").trim(),
          namePath:           `${userId}/Profile/${code}/`,
          welcomeMessagePath: `${userId}/Profile/${code}/`,
          // Always sent, even when empty. The server merges anything it does not receive, so
          // omitting this would make the three parts impossible to CLEAR once set.
          welcomeParts: {
            thanks:      (wp?.thanks      || "").trim(),
            positioning: (wp?.positioning || "").trim(),
            invitation:  (wp?.invitation  || "").trim(),
          },
          // The line offering THIS language to callers of another (Stage 4.1c); empty = the
          // assistant writes it.
          languageOfferText:  (profile.locales?.[code]?.languageOfferText || "").trim(),
        };
      }
    }

    // The offer switch lives beside the languages, not under one of them.
    localesObj.languageOffer = { enabled: profile.locales?.languageOffer?.enabled !== false };

    const dto = {
      UserId:              userId,
      LocalesJson:         JSON.stringify(localesObj),
      Languages:           profile.languages.join(","),
      DefaultLanguage:     typeof profile.dLanguage === "string"
                             ? profile.dLanguage
                             : (profile.languages[0] || "en"),
      WorkingDays:         profile.workingDays,
      WorkingHours:        profile.workingHours,
      IsCustomWorkingDays: profile.isCustomWorkingDays,
      Timezone:            profile.timezone || "UTC",
      audioFileNames:      [],
    };

    const formData = new FormData();
    formData.append("payload", JSON.stringify(dto));
    await axiosInstance.post("/Profile", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    // The zone just saved is the one every screen shows from now on.
    setBusinessTimeZone(dto.Timezone);
  }
);

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    setLocaleName: (state, action) => {
      const { lang, value } = action.payload;
      if (!state.profile.locales[lang]) state.profile.locales[lang] = { name: "", welcomeMessage: "" };
      state.profile.locales[lang].name = value;
    },
    setLocaleWelcome: (state, action) => {
      const { lang, value } = action.payload;
      if (!state.profile.locales[lang]) state.profile.locales[lang] = { name: "", welcomeMessage: "" };
      state.profile.locales[lang].welcomeMessage = value;
    },
    // The three-part welcome: thanks, positioning, invitation. The call path has preferred it over
    // the single message since it was added, and until now nothing could edit it, so a tenant on
    // three parts changed the single message and heard no difference.
    setLocaleWelcomePart: (state, action) => {
      const { lang, part, value } = action.payload;
      if (!state.profile.locales[lang]) state.profile.locales[lang] = { name: "", welcomeMessage: "" };
      const loc = state.profile.locales[lang];
      loc.welcomeParts = { thanks: "", positioning: "", invitation: "", ...(loc.welcomeParts || {}) };
      loc.welcomeParts[part] = value;
    },
    clearLocaleWelcomeParts: (state, action) => {
      const { lang } = action.payload;
      if (state.profile.locales[lang]) state.profile.locales[lang].welcomeParts = { thanks: "", positioning: "", invitation: "" };
    },
    setLocaleOfferText: (state, action) => {
      const { lang, value } = action.payload;
      if (!state.profile.locales[lang]) state.profile.locales[lang] = { name: "", welcomeMessage: "" };
      state.profile.locales[lang].languageOfferText = value;
    },
    setLanguageOfferEnabled: (state, action) => {
      state.profile.locales.languageOffer = { ...(state.profile.locales.languageOffer || {}), enabled: !!action.payload };
    },
    setDefaultWorkingDays: (state, action) => {
      state.profile.workingDays = action.payload.workingDays;
    },
    setWorkingDaysHours: (state, action) => {
      if (state.profile.workingDays[action.payload.day]) {
        if (action.payload.pType === "from")
          state.profile.workingDays[action.payload.day].from =
            action.payload.value;
        else
          state.profile.workingDays[action.payload.day].to =
            action.payload.value;
      }
    },
    setFromDefaultWorkingHours: (state, action) => {
      const oldFrom = state.profile.workingHours.from;
      state.profile.workingHours.from = action.payload.value;
      // Cascade to days that are still at the old default (not manually customized)
      Object.keys(state.profile.workingDays).forEach((day) => {
        if (state.profile.workingDays[day]?.from === oldFrom) {
          state.profile.workingDays[day].from = action.payload.value;
        }
      });
    },
    setToDefaultWorkingHours: (state, action) => {
      const oldTo = state.profile.workingHours.to;
      state.profile.workingHours.to = action.payload.value;
      Object.keys(state.profile.workingDays).forEach((day) => {
        if (state.profile.workingDays[day]?.to === oldTo) {
          state.profile.workingDays[day].to = action.payload.value;
        }
      });
    },
    // The cascade above deliberately spares a day the operator has customised. That is right, and
    // it is also how a change to the default can apply to four days and not the other three with
    // nothing said. This is the deliberate version of the same thing: every open day takes the
    // default, on request.
    applyDefaultToAllDays: (state) => {
      const { from, to } = state.profile.workingHours;
      Object.keys(state.profile.workingDays).forEach((day) => {
        if (state.profile.workingDays[day]) state.profile.workingDays[day] = { ...state.profile.workingDays[day], from, to };
      });
    },
    resetDayToDefault: (state, action) => {
      const { day } = action.payload;
      if (state.profile.workingDays[day]) {
        state.profile.workingDays[day].from = state.profile.workingHours.from;
        state.profile.workingDays[day].to   = state.profile.workingHours.to;
      }
    },
    setLanguages: (state, action) => {
      const v = action.payload.value;
      if (state.profile.languages.includes(v)) return;
      // A screen that forgets to disable the box must not be able to write a sixth language.
      if (state.profile.languages.length >= MAX_FLOW_LANGS) return;
      state.profile.languages.push(v);
    },
    removeLanguages: (state, action) => {
      state.profile.languages = state.profile.languages.filter(
        (lng) => lng != action.payload.value
      );
    },
    setDLanguage: (state, action) => {
      state.profile.dLanguage = action.payload.value;
      if (action.payload.value === "ar") state.profile.languages.sort();
      else state.profile.languages.sort().reverse();
    },
    setIsCustomWorkingDays: (state, action) => {
      state.profile.isCustomWorkingDays = action.payload.value;
    },
    setTimezone: (state, action) => {
      state.profile.timezone = action.payload.value;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProfile.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.status = "Success";
        if (action.payload !== null) {
          state.profile = action.payload;
        }
        // null = no profile row in DB yet; keep existing Redux state
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.status = "Rejected";
      });
  },
});

export const {
  setLocaleName,
  setLocaleWelcome,
  setLocaleWelcomePart,
  clearLocaleWelcomeParts, setLocaleOfferText, setLanguageOfferEnabled,
  setDefaultWorkingDays,
  setFromDefaultWorkingHours,
  setToDefaultWorkingHours,
  setWorkingDaysHours,
  applyDefaultToAllDays,
  resetDayToDefault,
  setLanguages,
  removeLanguages,
  setDLanguage,
  setIsCustomWorkingDays,
  setTimezone,
} = profileSlice.actions;
export default profileSlice.reducer;
